(ns app.front.view.bond.util
  "Pure helper/util functions for bond views."
  (:require [app.domain.bond.model :as bond-model]
            [app.domain.business.model :as business-model]
            [app.front.validate :as validate]
            [clojure.string :as str]
            [core.domain.validators :as v]
            [core.user.model :as user-model]
            [potpuri.core :as p]
            [schema.core :as s]))

(def SelectedBondOption (s/pred #(and (map? %) (some? (:id %))) 'option-selected))

(def base-error-messages
  "Base validation rules - always applied for non-invite flows."
  {:bond/template     [[SelectedBondOption :bond/validation-template-required]]
   :bond/external-id  [[validate/NonBlankString :bond/validation-external-id-required]]
   :bond/amount       [[validate/PositiveNumber :bond/validation-amount-positive]]
   :bond/expires-on   [[v/FutureDate :bond/validation-expires-future]]
   :bond/issuer-name  [[validate/NonBlankString :bond/validation-issuer-name-required]]
   :bond/description  [[validate/NonBlankString :bond/validation-description-required]]
   :bond/issuer-phone [[validate/BlankOrPhone :bond/validation-issuer-phone-invalid]]
   :bond/issuer-email [[validate/BlankOrEmail :bond/validation-issuer-email-invalid]]})

(def group-required-field-rules
  "Validation rules to add when fields are required by domain group"
  {:bond/issuer-phone [[validate/NonBlankString :bond/validation-issuer-phone-required]]
   :bond/issuer-email [[validate/NonBlankString :bond/validation-issuer-email-required]]})

(def workspace-required-field-rules
  "Validation rules when application is made from a workspace"
  {:bond/owner       [[SelectedBondOption :bond/validation-owner-required]]
   :bond/external-id [[validate/NonBlankString :bond/validation-external-id-required]]})

(def SelectedBusiness
  "Predicate for a selected business - must be a map with :business/unique-id"
  (s/pred #(and (map? %) (some? (:business/unique-id %))) 'business-selected))

(def group-business-owner-rules
  "Validation rules when owner is a business (group flow)"
  {:bond/owner [[SelectedBusiness :bond/validation-business-required]]})

(def group-user-owner-rules
  "Validation rules when owner is an individual user (group flow, no workspace)"
  {:bond/owner [[validate/Email :bond/validation-owner-email-required]]})

(def ^:private OptionalPositiveNumber
  "Accepts nil or a positive number."
  (s/maybe validate/PositiveNumber))

(def ^:private OptionalFutureDate
  "Accepts nil or a future date."
  (s/maybe v/FutureDate))

(def ^:private OptionalString
  "Accepts nil, blank string, or any string. Used for fields that can be cleared (blank → nil)."
  (s/maybe s/Str))

(def invite-base-error-messages
  "Validation rules for invite flow - only owner and template required.
   All string fields use OptionalString to allow clearing (blank → nil → retract).
   Phone/email use BlankOr* validators for format checking when provided."
  {:bond/owner        [[SelectedBondOption :bond/validation-owner-required]]
   :bond/template     [[SelectedBondOption :bond/validation-template-required]]
   :bond/external-id  [[OptionalString :bond/validation-external-id-invalid]]
   :bond/amount       [[OptionalPositiveNumber :bond/validation-amount-positive]]
   :bond/expires-on   [[OptionalFutureDate :bond/validation-expires-future]]
   :bond/description  [[OptionalString :bond/validation-description-invalid]]
   :bond/issuer-name  [[OptionalString :bond/validation-issuer-name-invalid]]
   :bond/issuer-phone [[validate/BlankOrPhone :bond/validation-issuer-phone-invalid]]
   :bond/issuer-email [[validate/BlankOrEmail :bond/validation-issuer-email-invalid]]})

(defn effective-error-messages
  "Merge base validation rules with context-specific field rules.
   - invite-flow?: uses relaxed validation rules (only template+owner required)
   - owner-optional?: skips owner validation (for pending-invite bonds without owner)
   - workspace-id: adds workspace-required-field-rules (includes external-id)
   - owner-type :bond.owner-type/business: overrides owner validation to SelectedBusiness
   - owner-type :bond.owner-type/user: overrides owner validation to Email
   - required-fields: adds required validation for optional issuer fields (set of field keys,
     ignored in invite flow)"
  [{:keys [required-fields workspace-id owner-type owner-optional? invite-flow?]
    :or   {required-fields #{}}}]
  (merge
    (cond-> (reduce (fn [messages field]
                      (if-let [required-rule (and (not invite-flow?)
                                                  (get group-required-field-rules field))]
                        (update messages field #(into required-rule %))
                        messages))
                    (if invite-flow? invite-base-error-messages base-error-messages)
                    required-fields)
      owner-optional? (dissoc :bond/owner))
    (cond
      workspace-id
      workspace-required-field-rules

      (= owner-type :bond.owner-type/business)
      group-business-owner-rules

      (= owner-type :bond.owner-type/user)
      group-user-owner-rules)))

(defn normalized-str
  "Normalize a string value for comparison: convert to string, trim, and return nil for empty."
  [v]
  (some-> v str str/trim not-empty))

(defn full-edit-dirty?
  "Check if the edit form has unsaved changes."
  [bond edit]
  (or (not= (get-in bond [:bond/template :db/id])
            (get-in edit [:bond/template :id]))
      (not= (:bond/amount bond)
            (:bond/amount edit))
      (not= (:bond/expires-on bond)
            (:bond/expires-on edit))
      (some #(not= (normalized-str (get bond %))
                   (normalized-str (get edit %)))
            bond-model/bond-application-string-keys)))

(defn partial-edit-dirty?
  "Check if the partial edit form (amount/expiration only) has unsaved changes."
  [bond amount expires-on]
  (or (not= amount (:bond/amount bond))
      (not= expires-on (:bond/expires-on bond))))

(defn form-data->api-params
  "Convert UI form data to backend API parameters.
   Only includes keys that are present in the input map.
   When keep-nil? false (default), skips nil/blank values.
   When keep-nil? true, sends explicit nil for cleared fields (for retraction)."
  ([data] (form-data->api-params data {:retract? false}))
  ([data {:keys [retract?]}]
   (let [assoc-fn (if retract? assoc p/assoc-if)]
     (reduce (fn [acc k]
               (if-not (contains? data k)
                 acc
                 (let [v (get data k)]
                   (if (bond-model/bond-application-string-keys k)
                     (assoc-fn acc k (normalized-str v))
                     (let [v-resolved (case k
                                        (:bond/owner :bond/template) (:id v)
                                        v)]
                       (assoc-fn acc k v-resolved))))))
             {}
             bond-model/bond-application-keys))))

(defn pending-invite-form-data->api-params
  "Convert UI form data to backend API parameters for pending-invite bonds.
   Sends explicit nil for cleared fields so the backend can retract values."
  [data]
  (form-data->api-params data {:retract? true}))

(defn format-bond-owner
  "For pending-invite bonds, shows invited business (user group) or user name if available."
  [lang owner-or-bond _tr]
  (case (:entity/type owner-or-bond)
    :entity.type/business (business-model/format-name owner-or-bond lang)
    :entity.type/user     (user-model/full-name owner-or-bond)
    ;; For bonds (pending-invite), show business name via the task's user-group (admin group),
    ;; which carries the business name as :group/long-name. Fall back to invited user's name.
    :entity.type/bond     (or (some-> (bond-model/claim-task-user-group owner-or-bond)
                                      :group/long-name)
                              (some-> (bond-model/claim-task-user owner-or-bond)
                                      user-model/full-name))
    nil))
